import React, { ReactNode, useCallback } from 'react';
import { useIntl } from 'react-intl';
import Checkbox from '@rio-cloud/rio-uikit/Checkbox';
import NoData from '@rio-cloud/rio-uikit/NoData';
import NotFoundState from '@rio-cloud/rio-uikit/NotFoundState';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import TableCol from '@rio-cloud/rio-uikit/TableCol';
import TableToolbar from '@rio-cloud/rio-uikit/TableToolbar';
import isEmpty from 'lodash/fp/isEmpty';

import Pagination from '@/features/common/components/maintenance-table/Pagination';
import { TableHeader } from '@/features/common/components/maintenance-table/TableHeader';
import { TableRow } from '@/features/common/components/maintenance-table/TableRow';
import {
    ColumnConfig,
    DetailViewTabParam,
    QueryState,
    ResourceBasicData,
    RowSelectionProps,
    RowStatusMapper,
} from '@/features/common/components/maintenance-table/types';
import { CommonSortBy } from '@/features/common/hooks/useCommonQueryParams';
import { PaginationHandling } from '@/features/common/hooks/usePagination';
import { SortHandling } from '@/features/common/hooks/useSorting';
import { PageMetadata } from '@/generated/GeneratedApiTypes';

export const renderNullable = (value?: any, messageKey?: string) => {
    const intl = useIntl();
    return value ? (
        <span>{value}</span>
    ) : (
        <NoData
            text={intl.formatMessage({ id: messageKey ?? 'intl-msg:vehicle-diagnostics.maintenance.table.noData' })}
        />
    );
};

export const accountRenderer = ({ account }: ResourceBasicData) => {
    return renderNullable(account.name);
};

export const vehicleRenderer = ({ asset }: ResourceBasicData) => {
    return renderNullable(asset.name);
};

export const vinRenderer = ({ asset }: ResourceBasicData) => {
    return renderNullable(asset.vin);
};

export const workshopRenderer = ({ workshop }: ResourceBasicData) => {
    return renderNullable(workshop?.name);
};

export const DEFAULT_COLUMNS_CONFIG: ColumnConfig[] = [
    {
        name: 'vehicle',
        labelKey: 'intl-msg:vehicle-diagnostics.maintenance.table.header.vehicle',
        hidden: false,
        sortable: true,
        renderer: vehicleRenderer,
    },
    {
        name: 'vin',
        labelKey: 'intl-msg:vehicle-diagnostics.maintenance.table.header.vin',
        hidden: false,
        sortable: true,
        renderer: vinRenderer,
    },
    {
        name: 'customer',
        labelKey: 'intl-msg:vehicle-diagnostics.maintenance.table.header.customer',
        hidden: false,
        sortable: true,
        renderer: accountRenderer,
    },
    {
        name: 'workshop',
        labelKey: 'intl-msg:vehicle-diagnostics.maintenance.table.header.workshop',
        hidden: true,
        sortable: true,
        renderer: workshopRenderer,
    },
];

interface MaintenanceTableProps<S> {
    columnConfig: ColumnConfig[];
    enableRowSelection?: boolean;
    rowSelection?: RowSelectionProps;
    queryState: QueryState;
    pageMetadata?: PageMetadata;
    pageSizeOptions?: number[];
    pagination: PaginationHandling;
    sorting: SortHandling<S>;
    rows?: any[];
    leftToolbar?: React.ReactNode;
    rightToolbar?: React.ReactNode;
    rowStatusMapper?: RowStatusMapper;
    rowDetailRenderer: (data: any) => ReactNode;
    detailViewTabParam: DetailViewTabParam;
    className?: string;
}

export const MaintenanceTable = <S = CommonSortBy,>({
    columnConfig,
    enableRowSelection = false,
    rowSelection,
    queryState,
    pageMetadata,
    pageSizeOptions,
    pagination,
    sorting,
    rows,
    leftToolbar,
    rightToolbar,
    rowStatusMapper = (_) => '',
    rowDetailRenderer,
    detailViewTabParam,
    className = '',
}: MaintenanceTableProps<S>) => {
    const intl = useIntl();
    const { isLoading, isFetching, isError, error } = queryState;
    const selectionEnabled = enableRowSelection && !!rowSelection;
    const columnCount = columnConfig.length + (selectionEnabled ? 1 : 0);
    const handleSortChange = useCallback(
        (event: React.MouseEvent<HTMLTableCellElement>) => {
            const newSortBy = event.currentTarget.dataset.sortby;
            if (newSortBy) {
                sorting.setSortBy(newSortBy as S);
            }
            if (newSortBy === sorting.sortBy) {
                sorting.toggleDirection();
            } else {
                sorting.setSortDirection('ASC');
            }
        },
        [sorting.sortBy],
    );
    return (
        <div className={`maintenance-table-container ${className}`}>
            <TableToolbar>
                <div className="table-toolbar-container">
                    <div className="table-toolbar-group-left">{leftToolbar}</div>
                    <div className="table-toolbar-group-right">{rightToolbar}</div>
                </div>
            </TableToolbar>
            <div className="table-responsive">
                <table className="maintenance-table table table-hover table-bordered">
                    <colgroup>
                        {selectionEnabled && <TableCol key="__select__" />}
                        {columnConfig.map((column) => (
                            <TableCol key={column.name} />
                        ))}
                    </colgroup>
                    <TableHeader
                        columns={columnConfig}
                        sortBy={sorting.sortBy}
                        sortDirection={sorting.sortDirection}
                        handleSortChange={handleSortChange}
                        selectionHeader={
                            selectionEnabled ? (
                                <Checkbox
                                    checked={rowSelection!.allSelected}
                                    indeterminate={rowSelection!.someSelected && !rowSelection!.allSelected}
                                    onChange={rowSelection!.onToggleAll}
                                    aria-label={intl.formatMessage({
                                        id: 'intl-msg:vehicle-diagnostics.maintenance.table.selectAll',
                                    })}
                                />
                            ) : undefined
                        }
                    />
                    <tbody>
                        {(isLoading || isFetching) && (
                            <tr>
                                <td colSpan={columnCount}>
                                    <Spinner text={'Loading...'} />
                                </td>
                            </tr>
                        )}
                        {!isLoading && !isFetching && isError && (
                            <tr>
                                <td colSpan={columnCount}>
                                    <NotFoundState
                                        outerClassName="border-none"
                                        headline={error?.status === 404 ? 'No data found' : 'Something went wrong'}
                                        message={
                                            error?.status === 404 ? 'Please refine your search' : 'Something went wrong'
                                        }
                                    />
                                </td>
                            </tr>
                        )}
                        {!isLoading && !isFetching && !isError && !isEmpty(rows) && (
                            <TableRow
                                rowStatusMapper={rowStatusMapper}
                                rows={rows}
                                columns={columnConfig}
                                rowDetailRenderer={rowDetailRenderer}
                                detailViewTabParam={detailViewTabParam}
                                rowSelection={selectionEnabled ? rowSelection : undefined}
                            />
                        )}
                        {!isLoading && !isFetching && !isError && isEmpty(rows) && (
                            <tr>
                                <td colSpan={columnCount}>
                                    <NotFoundState
                                        outerClassName="border-none"
                                        headline="Nothing found"
                                        message="Please refine your search"
                                    />
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <Pagination
                pageMetadata={isError ? undefined : pageMetadata}
                onPageChange={pagination.setPageNumber}
                onPageSizeChange={pagination.setPageSize}
                pageSizeOptions={pageSizeOptions}
            />
        </div>
    );
};
